import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';
import { deleteVoiceCapture } from '../utils/audioStorage';

const STORAGE_KEY = 'inbox';

/**
 * Best-effort cleanup of any Cloud-Storage-backed audio attachments that
 * have stopped being referenced by any inbox item — otherwise a voice
 * capture's Storage object lingers forever (storage.rules never
 * auto-expires users/{uid}/voice/ objects). Used both when a whole item is
 * removed and when an update replaces its attachments (see updateInboxItem).
 * Never throws and never awaited by callers: the user-visible action must
 * succeed even if this cleanup fails (offline, already gone, a permission
 * hiccup, etc).
 */
function _cleanupAttachments(attachments) {
    (attachments || []).forEach(att => {
        if (att?.type === 'audio' && att?.data) {
            deleteVoiceCapture(att.data).catch(() => {});
        }
    });
}

const useInboxStore = create((set, get) => ({
    items: storage.get(STORAGE_KEY, []),

    _persist() {
        storage.set(STORAGE_KEY, get().items);
    },

    addInboxItem(content, source = 'quick_capture', options = {}) {
        const isRich = typeof content === 'object' && content !== null;
        const contentType = options.contentType || (isRich ? 'rich' : 'plain');
        
        let text = '';
        if (typeof content === 'string') {
            text = content;
        } else if (isRich) {
            const traverse = (node) => {
                if (!node) return;
                if (node.type === 'text' && typeof node.text === 'string') {
                    text += (text ? ' ' : '') + node.text;
                }
                if (Array.isArray(node.content)) {
                    node.content.forEach(traverse);
                } else if (Array.isArray(node)) {
                    node.forEach(traverse);
                }
            };
            traverse(content);
        }
        
        const wordCount = text.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0;
        const hasFormatting = contentType === 'rich';

        const newItem = {
            id: crypto.randomUUID(),
            content, // Can be raw TipTap JSON or plain string
            source,
            createdAt: new Date().toISOString(),
            status: 'unread',
            contextProjectId: options.projectId || null,
            contextAreaId: options.areaId || null,
            contentType,
            attachments: options.attachments || [],
            metadata: {
                wordCount,
                hasFormatting,
            }
        };
        
        set(state => ({ items: [...state.items, newItem] }));
        get()._persist();
        return newItem;
    },

    updateInboxItem(id, updates) {
        const before = get().items.find(i => i.id === id);
        set(state => ({
            items: state.items.map(item =>
                item.id === id ? { ...item, ...updates } : item
            )
        }));
        get()._persist();
        // No current caller replaces `attachments` on an existing item, but
        // if a future one does (e.g. an edit flow that lets you remove a
        // voice note from an otherwise-kept item), any audio attachment that
        // existed before and isn't in the new list would otherwise become an
        // orphaned Storage object with nothing left to ever clean it up —
        // the exact failure mode _cleanupAttachments exists to prevent on
        // delete. Diffing by `data` (the Storage URL) means an attachment
        // that's merely reordered or has other fields touched is correctly
        // left alone.
        if (before && Array.isArray(updates.attachments)) {
            const kept = new Set(updates.attachments.map(att => att?.data));
            _cleanupAttachments((before.attachments || []).filter(att => !kept.has(att?.data)));
        }
    },

    deleteInboxItem(id) {
        const item = get().items.find(i => i.id === id);
        set(state => ({
            items: state.items.filter(item => item.id !== id)
        }));
        get()._persist();
        if (item) _cleanupAttachments(item.attachments);
    },

    clearProcessed() {
        const toRemove = get().items.filter(item => item.status !== 'unread');
        set(state => ({
            items: state.items.filter(item => item.status === 'unread')
        }));
        get()._persist();
        toRemove.forEach(item => _cleanupAttachments(item.attachments));
    },
    
    getUnreadCount() {
        return get().items.filter(item => item.status === 'unread').length;
    }
}));

onRehydrate(() => {
    useInboxStore.setState({ items: storage.get(STORAGE_KEY, []) });
});

export default useInboxStore;
